// Pure helpers for ContractEditModal — extracted so vitest can
// drive the diff-only payload logic and the date guard without
// mounting the React component.
//
// The modal renders form state as YYYY-MM-DD (HTML <input
// type="date">), but the server expects ISO 8601 timestamps.
// These helpers handle the round-trip safely.

import type { UpdateContractBody } from '@/api/contracts';
import { kstYmd } from '@/lib/format-date';

export interface InitialContractHeader {
  /**
   * Server may return ISO 8601 string OR Date instance depending on
   * serialization (Prisma transformer / SuperJSON). Accept both —
   * helpers below normalize to YYYY-MM-DD via shared `toYmd`.
   */
  startDate: string | Date;
  endDate: string | Date;
  durationMonths: string;
  renewalScheduledAt: string | Date | null;
}

export interface DraftEdit {
  /** YYYY-MM-DD form-state values from the date inputs. */
  startDate: string;
  endDate: string;
  durationMonths: string;
  renewalScheduledAt: string;
}

/**
 * Trim an ISO timestamp / Date instance (or null) to a YYYY-MM-DD
 * form value. Delegates to shared `kstYmd` so Date instances from the
 * API don't crash with `.slice is not a function`.
 *
 * 추가수정 2차(26-07-27) §2 — **계약일 하루 밀림 수정**. 예전에는
 * `toYmd`(= ISO 를 UTC 로 잘라내기)였는데, 계약 시작/종료일은 등록 마법사가
 * `ymdToIso`(= 한국 자정 → `T15:00:00.000Z` 전날)로 저장한다. 그래서 읽을 때
 * UTC 로 자르면 운영자가 고른 날보다 **하루 이르게** 복원됐다(같은 계약일이
 * 고객사 마스터에서는 맞고 계약 화면에서는 밀려 보이던 원인).
 * KST 로 복원하면 저장(한국 자정)과 대칭이 되고, 서버가 UTC 자정으로 준
 * 값(=한국 09:00)도 같은 날짜로 나와 두 저장 관례 모두 안전하다.
 *
 * 반환은 `<input type="date">` 가 요구하는 하이픈 표기 — kstYmd 의 점 표기를
 * 바꿔 쓴다(표시용 정본을 한 번 더 구현하지 않기 위함).
 */
export function isoToDate(iso: string | Date | null): string {
  return kstYmd(iso)?.replace(/\./g, '-') ?? '';
}

/**
 * Returns null when the form is valid; otherwise an
 * operator-readable Korean message that matches the modal's UX.
 *
 * Mirrors the server-side `endDate > startDate` CHECK so we
 * fail fast instead of bouncing on a 400.
 */
export function checkEditForm(form: DraftEdit): string | null {
  if (form.endDate && form.startDate && new Date(form.endDate) <= new Date(form.startDate)) {
    return '종료일은 시작일보다 뒤여야 합니다';
  }
  return null;
}

/**
 * Build the diff-only `PATCH /contracts/:id` payload — only
 * fields whose form value differs from the initial value.
 *
 * `renewalScheduledAt` round-trips through `null` when the
 * operator clears the input (so BR-CNT-03's auto-derivation
 * re-applies on the server). Other fields are simple ISO
 * conversions.
 *
 * Returns an empty object when nothing changed; the caller is
 * expected to check for that before firing the mutation so the
 * UI can surface "변경 사항이 없습니다".
 */
export function buildDiffPayload(
  initial: InitialContractHeader,
  form: DraftEdit,
): UpdateContractBody {
  const body: UpdateContractBody = {};

  if (isoToDate(initial.startDate) !== form.startDate) {
    body.startDate = new Date(form.startDate).toISOString();
  }
  if (isoToDate(initial.endDate) !== form.endDate) {
    body.endDate = new Date(form.endDate).toISOString();
  }
  if (initial.durationMonths !== form.durationMonths) {
    body.durationMonths = form.durationMonths;
  }
  if (isoToDate(initial.renewalScheduledAt) !== form.renewalScheduledAt) {
    body.renewalScheduledAt = form.renewalScheduledAt
      ? new Date(form.renewalScheduledAt).toISOString()
      : null;
  }

  return body;
}
